import { apiGet, apiPost, apiPut } from '../utils/api';

/**
 * Therapist Notes Service Class
 * Handles all therapist notes-related API calls and data management
 */
class NotesService {
  constructor() {
    this.baseUrl = '/.netlify/functions';
  }

  /**
   * Get all notes for a patient
   * @param {Object} user - Current user
   * @param {string} patientId - Patient ID
   * @returns {Promise<Object>} Notes data
   */
  async getNotes(user, patientId) {
    const url = `${this.baseUrl}/get-therapist-notes?patientId=${patientId}`;
    const response = await apiGet(url);
    return response;
  }

  /**
   * Get a single note by ID
   * @param {Object} user - Current user
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>} Note data with full content
   */
  async getNote(user, noteId) {
    const url = `${this.baseUrl}/get-therapist-note?noteId=${noteId}`;
    const response = await apiGet(url);
    return response;
  }

  /**
   * Create a new note
   * @param {Object} user - Current user
   * @param {string} patientId - Patient ID
   * @param {Object} noteData - Note data
   * @param {string} noteData.heading - Note heading
   * @param {string} noteData.summary - Note summary
   * @param {Object|string} noteData.content - TipTap JSON content
   * @param {string[]} noteData.tags - Array of tags
   * @returns {Promise<Object>} Created note
   */
  async createNote(user, patientId, noteData) {
    const response = await apiPost(
      `${this.baseUrl}/create-therapist-note`,
      {
        patientId,
        heading: noteData.heading,
        summary: noteData.summary,
        content: noteData.content,
        tags: noteData.tags || []
      }
    );
    return response;
  }

  /**
   * Update an existing note
   * @param {Object} user - Current user
   * @param {string} noteId - Note ID
   * @param {Object} noteData - Updated note data
   * @returns {Promise<Object>} Updated note
   */
  async updateNote(user, noteId, noteData) {
    const response = await apiPut(
      `${this.baseUrl}/update-therapist-note`,
      {
        noteId,
        heading: noteData.heading,
        summary: noteData.summary,
        content: noteData.content,
        tags: noteData.tags
      }
    );
    return response;
  }

  /**
   * Get all tags used by the therapist (for autocomplete)
   * @param {Object} user - Current user
   * @returns {Promise<Object>} Tags data
   */
  async getTherapistTags(user) {
    const url = `${this.baseUrl}/get-therapist-note-tags`;
    const response = await apiGet(url);
    return response;
  }

  /**
   * Validate note data
   * @param {Object} noteData - Note data to validate
   * @returns {boolean} Is valid
   */
  validateNoteData(noteData) {
    if (!noteData.heading || !noteData.heading.trim()) {
      throw new Error('Heading is required');
    }

    if (noteData.heading.trim().length > 200) {
      throw new Error('Heading must be less than 200 characters');
    }

    if (!noteData.summary || !noteData.summary.trim()) {
      throw new Error('Summary is required');
    }

    if (noteData.summary.trim().length > 500) {
      throw new Error('Summary must be less than 500 characters');
    }

    if (!noteData.content) {
      throw new Error('Content is required');
    }

    // Validate TipTap content structure
    try {
      const content = typeof noteData.content === 'string' 
        ? JSON.parse(noteData.content) 
        : noteData.content;
      
      if (!content.type || !content.content) {
        throw new Error('Invalid TipTap content format');
      }
    } catch (error) {
      throw new Error('Content must be valid TipTap JSON');
    }

    // Validate tags
    if (noteData.tags && Array.isArray(noteData.tags)) {
      if (noteData.tags.length > 10) {
        throw new Error('Maximum 10 tags allowed');
      }
      noteData.tags.forEach(tag => {
        if (typeof tag !== 'string' || !tag.trim()) {
          throw new Error('Tags must be non-empty strings');
        }
        if (tag.trim().length > 50) {
          throw new Error('Each tag must be less than 50 characters');
        }
      });
    }

    return true;
  }

  /**
   * Format date for display
   * @param {string|Date} dateInput - Date string or Date object
   * @returns {string} Formatted date string
   */
  formatDateForDisplay(dateInput) {
    if (!dateInput) return 'N/A';
    
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }
}

// Create singleton instance
const notesService = new NotesService();

export default notesService;

